import {
  DutyStatus,
  IRequestAccessibilityFeature,
  IRequestRiderType,
  RequestDispatchStatus,
} from '@sparelabs/domain-logic'
import { ILineString, IPoint } from '@sparelabs/geography'
import { IListModelQueryParamValues, IModelJsonValues } from '@sparelabs/http-client-utils'
import { DeviceOS } from './DeviceTypes'
import { DutyCancellationReason, DutyCancelledBy, IDutyCancellationResponse } from './DutyCancellationTypes'
import { IFleetStub } from './FleetTypes'
import { IDutyScheduleLeg } from './RouteOptimizationTypes'
import { IUserStub } from './UserTypes'
import { IVehicleStub } from './VehicleTypes'
/**
 * @deprecated use from @sparelabs/domain-logic instead
 */
export { DutyStatus }

export interface IRouteAssignmentInput {
  routeId: string
  startTs: number
}

export interface IDutiesPostBody {
  data: IDutyPostBody[]
}

export interface IDutyPostBody {
  fleetId: string
  identifier?: string
  driverId?: string
  vehicleId: string
  startRequestedTs?: number
  startFlexibility?: number
  startLocation?: IPoint
  startAddress?: string
  endRequestedTs?: number
  endFlexibility?: number
  endLocation?: IPoint
  endAddress?: string
  matchingLogic?: DutyMatchingLogic
  status?: DutyStatus
  ownerType?: DutyOwnerType
  routeAssignments?: IRouteAssignmentInput[]
  metadata?: Record<string, unknown>
}

export interface IDutyPatchBodySentToRos {
  vehicleId?: string
  startRequestedTs?: number
  startLocation?: IPoint
  startAddress?: string
  endRequestedTs?: number
  endAddress?: string
  endLocation?: IPoint
}

export interface IDutyPatchBody extends IDutyPatchBodySentToRos {
  identifier?: string | null
  driverId?: string | null
  isMatchingEnabled?: boolean
  matchingDisabledEndTs?: number | null
  metadata?: Record<string, unknown>
  forceEdit?: boolean
  preDutyEamInspectionId?: string | null
  postDutyEamInspectionId?: string | null
}

export enum DutyBulkMode {
  SelectByIds = 'selectByIds',
  SelectByDates = 'selectByDates',
}

export interface IDutyBulkCopyByIdsPostBody {
  mode: DutyBulkMode.SelectByIds
  sourceDate: string
  targetDates: string[]
  includeDriverAssignment: boolean
  includeRouteAssignment: boolean
  includeBreakAssignment: boolean
  dutyIds: string[]
}

export interface IDutyBulkCopyByDatesPostBody {
  mode: DutyBulkMode.SelectByDates
  sourceDate: string
  targetDates: string[]
  includeDriverAssignment: boolean
  includeRouteAssignment: boolean
  includeBreakAssignment: boolean
  excludeDutyIds: string[]
  fleetIds: string[]
}

export interface IDutyBulkCancelPostBody {
  dutyIds: string[]
  cancelledBy: DutyCancelledBy
  reason: DutyCancellationReason
  notes: string | null
}

export type IDutyBulkCopyPostBody = IDutyBulkCopyByIdsPostBody | IDutyBulkCopyByDatesPostBody

export interface IDutyBulkDeleteByIdsPostBody {
  mode: DutyBulkMode.SelectByIds
  dutyIds: string[]
}

export interface IDutyBulkDeleteByDatesPostBody {
  mode: DutyBulkMode.SelectByDates
  date: string
  excludeDutyIds: string[]
  fleetIds: string[]
}

export type IDutyBulkDeletePostBody = IDutyBulkDeleteByIdsPostBody | IDutyBulkDeleteByDatesPostBody

export enum DutyOwnerType {
  Organization = 'organization',
  User = 'user',
}

export enum DutyMatchingLogic {
  AutoAccept = 'autoAccept',
  AcceptReject = 'acceptReject',
}

export interface IDutyExternalDriverData {
  firstName: string | null
  photoUrl: string | null
  phoneNumber: string | null
}

export interface IDutyResponse extends IModelJsonValues {
  identifier?: string | null
  fleetId: string
  fleet: IFleetStub | null
  vehicleId: string
  vehicle: IVehicleStub | null
  driverId: string | null
  driver: IUserStub | null
  startRequestedTs: number
  endRequestedTs?: number
  startLocation: IPoint | null
  endLocation: IPoint | null
  startAddress: string | null
  endAddress: string | null
  startFlexibility: number
  endFlexibility: number
  status: DutyStatus
  matchingLogic: DutyMatchingLogic
  isDeletable: boolean
  isMatchingEnabled: boolean
  hasVehicleConflict: boolean
  hasDriverConflict: boolean
  matchingDisabledEndTs: number | null
  lateness: number | null
  ownerType: DutyOwnerType
  externalDriverData: IDutyExternalDriverData | null
  appVersion: string | null
  deviceOS: DeviceOS | null
  metadata: Record<string, unknown>
  metrics: IDutyPrecomputedMetricsData | null
  scheduleLegs: IDutyScheduleLeg[]
  preDutyEamInspectionId: string | null
  postDutyEamInspectionId: string | null
  cancellationDetails: IDutyCancellationResponse | null
}

export interface IDutyPrecomputedMetricsData {
  firstSeenInServiceAreaTs: number | null
  lastSeenInServiceAreaTs: number | null
  /**
   * Revenue hour is calculated by duration from first pickup completedTs to last dropoff completedTs (seconds)
   * TODO: Remove this field once all clients are using totalRevenueHoursSV2
   */
  totalRevenueHoursS: number
  /**
   * Revenue hour is calculated by duration from first pickup arrivedTs to last dropoff completedTs (seconds)
   */
  totalRevenueHoursSV2: number
  totalBreakLengthS: number
  totalInProgressPauseLengthS: number
  totalTravelDistanceM: number
  totalDistanceWithoutRidersM: number
  totalRevenueDistanceM: number
  totalBreakDistanceM: number
}

export interface IScheduledDutyResponse extends IDutyResponse {
  endRequestedTs: number
}

export interface IDutyQueryParams extends IListModelQueryParamValues {
  fromStartRequestedTs?: number
  toStartRequestedTs?: number
  fromEndRequestedTs?: number
  toEndRequestedTs?: number
  withinRangeFromTs?: number
  withinRangeToTs?: number
  driverId?: string
  vehicleId?: string
  status?: DutyStatus
  fleetId?: string | string[]
  external?: boolean
  metadata?: Record<string, unknown>
}

export enum DutyOrderBy {
  CreatedAt = 'createdAt',
  StartRequestedTs = 'startRequestedTs',
  EndRequestedTs = 'endRequestedTs',
  Identifier = 'identifier',
  VehicleId = 'vehicleId',
  DriverId = 'driverId',
  Vehicle = 'vehicle',
  Driver = 'driver',
}

export interface IDutyScheduleDutiesQueryParams extends IListModelQueryParamValues {
  nowTs: number
  withinRangeFromTs: number
  withinRangeToTs: number
  fleetId?: string[]
  status?: DutyStatus[]
  orderBy?: DutyOrderBy
}

export interface IDutyScheduleRequestsQueryParams extends IListModelQueryParamValues {
  dutyIds: string[]
}

export interface IDutyLatenessResponse {
  id: string
  createdAt: number
  dutyId: string
  lateness: number
}

export interface IDutyStubResponse
  extends Pick<
    IDutyResponse,
    'fleetId' | 'startRequestedTs' | 'endRequestedTs' | 'isMatchingEnabled' | 'matchingDisabledEndTs' | 'identifier'
  > {
  driverStub: IUserStub | null
  vehicleStub: IVehicleStub
  dutyId: string
  externalDriverData: IDutyExternalDriverData | null
}

export enum DutyScheduleProblem {
  Unassigned = 'unassigned',
  LatePullout = 'latePullout',
  IndefinitelyPaused = 'indefinitelyPaused',
  VehicleConflict = 'vehicleConflict',
  DriverConflict = 'driverConflict',
  VehicleNotActive = 'vehicleNotActive',
}
export interface IDutyScheduleDutyResponse {
  id: string
  identifier: string | null
  vehicleId: string
  vehicleIdentifier: string
  driverId: string | null
  driverName: string | null
  version: number
  problems: DutyScheduleProblem[] | undefined
}

export interface IDutyScheduleRequestResponse {
  id: string
  dutyId: string
  dispatchStatus: RequestDispatchStatus | null
  startTime: number
  endTime: number
  relevantLateness: number
  riderId: string | null
  riderFirstName: string | null
  riderLastName: string | null
  riders: IRequestRiderType[]
  accessibilityFeatures: IRequestAccessibilityFeature[]
  serviceName: string
}

export interface IDutyStubQueryParams extends IListModelQueryParamValues {
  status?: DutyStatus
}

export interface IDutyPolylineResponse {
  polyline: ILineString | null
}

export interface IDutyPolylineQueryParams {
  nextSlotOnly?: boolean
}

export type IDutiesSearchQueryParams = IDutiesSearchStringQueryParams | IDutiesSearchIdQueryParams

export interface IDutiesSearchStringQueryParams {
  search: string
  fromStartRequestedTs?: number
  toStartRequestedTs?: number
  withinRangeFromTs?: number
  withinRangeToTs?: number
  status?: DutyStatus | DutyStatus[]
  fleetId?: string | string[]
  limit?: number
}
export interface IDutiesSearchIdQueryParams {
  ids: string[]
  limit?: number
}

export interface IDutySearchResponseItem {
  id: string
  identifier?: string | null
  driverId: string | null
  vehicleId: string
  fleetId: string
  status: DutyStatus
  startLocation: IPoint | null
  startAddress: string | null
  startRequestedTs: number
  endLocation: IPoint | null
  endAddress: string | null
  endRequestedTs: number | null
  driverFirstName: string | null
  driverLastName: string | null
  driverPhoneNumber: string | null
  vehicleIdentifier: string
  vehicleMake: string
  vehicleModel: string
  vehicleColor: string
  vehicleLicensePlate: string
  fleetName: string
}

export interface IDutiesSearchResponse {
  data: IDutySearchResponseItem[]
}

export enum DriverActivity {
  Boarding = 'boarding',
  Transporting = 'transporting',
  Deadheading = 'deadheading',
  OnBreak = 'onBreak',
  Idle = 'idle',
}

export interface IDutyExportQueryParams {
  fleetIds: string[]
  startOfDayTs: number
  endOfDayTs: number
}

export interface IDutyExportBreak {
  startTime: number
  endTime: number
  duration: number
  earliestStartTime: number | null
  earliestEndTime: number | null
  latestStartTime: number | null
  latestEndTime: number | null
  location: string | null
  reason: string | null
}

export interface IDutyExportData {
  id: string
  fleetId: string
  vehicleId: string
  driverId: string | null
  matchingDisabledEndTs: number | null
  startAddress: string | null
  startLocation: IPoint | null
  endAddress: string | null
  endLocation: IPoint | null
  startRequestedTs: number
  endRequestedTs: number | null
  scheduleLegs: IDutyScheduleLeg[]

  // Enhanced fields
  driverName: string | null
  vehicleIdentifier: string | null
  fleetName: string | null
  breaks: IDutyExportBreak[]

  // Additional timestamp fields for export
  startRequestedTime?: string
  endRequestedTime?: string
  matchingDisabledEndTime?: string
  [key: string]: any // For custom fields
}
